import {
	CONDITIONAL_INLINE_NODE,
	conditionalBlock,
} from '@dealstack/frontend/shared/components/EmailTemplates/extensions/conditionalNodes';
import type { JSONContent } from '@tiptap/core';

const TAG = /\{%[^%]*%\}/;
const ALL_TAGS = /\{%[^%]*%\}/g;
const IF_TAG = /^\{%\s*if\s+([A-Za-z_][A-Za-z0-9_]*)\s*%\}$/;
const ELSE_TAG = /^\{%\s*else\s*%\}$/;
const ENDIF_TAG = /^\{%\s*endif\s*%\}$/;

const BLOCK_CONTAINERS = new Set([
	'doc',
	'body',
	'container',
	'section',
	'div',
	'blockquote',
	'listItem',
	'columnsColumn',
	'tableCell',
	'tableHeader',
	'conditionalBranch',
]);

type Tag = { kind: 'if'; variable: string } | { kind: 'else' } | { kind: 'endif' };

const parseTag = (raw: string): Tag | null => {
	const ifMatch = IF_TAG.exec(raw);
	if (ifMatch) {
		return { kind: 'if', variable: ifMatch[1] };
	}
	if (ELSE_TAG.test(raw)) {
		return { kind: 'else' };
	}
	if (ENDIF_TAG.test(raw)) {
		return { kind: 'endif' };
	}
	return null;
};

export const flatText = (node: JSONContent): string => {
	if (node.type === 'text') {
		return node.text ?? '';
	}
	return (node.content ?? []).map(flatText).join('');
};

const tagsOf = (node: JSONContent): (Tag | null)[] =>
	[...flatText(node).matchAll(ALL_TAGS)].map((match) => parseTag(match[0]));

const stripTags = (node: JSONContent): JSONContent => {
	if (node.type === 'text') {
		return { ...node, text: (node.text ?? '').replace(ALL_TAGS, '') };
	}
	if (!node.content) {
		return node;
	}
	return {
		...node,
		content: node.content
			.map(stripTags)
			.filter((child) => child.type !== 'text' || (child.text ?? '') !== ''),
	};
};

const trimTrailingSpace = (node: JSONContent): JSONContent => {
	const content = [...(node.content ?? [])];
	const last = content[content.length - 1];
	if (last?.type === 'text') {
		const text = (last.text ?? '').replace(/\s+$/, '');
		if (text === '') {
			content.pop();
		} else {
			content[content.length - 1] = { ...last, text };
		}
	}
	return { ...node, content };
};

const holdsNothing = (node: JSONContent): boolean => (node.content ?? []).length === 0;

const convertInlineTags = (block: JSONContent): JSONContent | null => {
	type Piece = { tag: Tag } | { node: JSONContent };
	const pieces: Piece[] = [];

	for (const child of block.content ?? []) {
		const text = child.type === 'text' ? child.text ?? '' : '';
		if (!TAG.test(text)) {
			pieces.push({ node: child });
			continue;
		}
		let cursor = 0;
		for (const match of text.matchAll(ALL_TAGS)) {
			const tag = parseTag(match[0]);
			if (!tag) {
				return null;
			}
			if (match.index > cursor) {
				pieces.push({ node: { ...child, text: text.slice(cursor, match.index) } });
			}
			pieces.push({ tag });
			cursor = match.index + match[0].length;
		}
		if (cursor < text.length) {
			pieces.push({ node: { ...child, text: text.slice(cursor) } });
		}
	}

	const before: JSONContent[] = [];
	const ifSide: JSONContent[] = [];
	const elseSide: JSONContent[] = [];
	const after: JSONContent[] = [];
	let variable: string | null = null;
	let phase: 'before' | 'if' | 'else' | 'after' = 'before';

	for (const piece of pieces) {
		if ('tag' in piece) {
			if (piece.tag.kind === 'if' && phase === 'before') {
				variable = piece.tag.variable;
				phase = 'if';
			} else if (piece.tag.kind === 'else' && phase === 'if') {
				phase = 'else';
			} else if (piece.tag.kind === 'endif' && (phase === 'if' || phase === 'else')) {
				phase = 'after';
			} else {
				return null;
			}
			continue;
		}
		if (phase === 'before') {
			before.push(piece.node);
		} else if (phase === 'if') {
			ifSide.push(piece.node);
		} else if (phase === 'else') {
			elseSide.push(piece.node);
		} else {
			after.push(piece.node);
		}
	}

	if (phase !== 'after' || variable === null || ifSide.length === 0) {
		return null;
	}
	if (elseSide.some((node) => node.type !== 'text')) {
		return null;
	}

	return {
		...block,
		content: [
			...before,
			{
				type: CONDITIONAL_INLINE_NODE,
				attrs: { variable, elseText: elseSide.map((node) => node.text ?? '').join('') },
				content: ifSide,
			},
			...after,
		],
	};
};

type OpenBlock = { variable: string; ifContent: JSONContent[]; elseContent: JSONContent[] | null };

const convertBlockList = (children: JSONContent[]): JSONContent[] | null => {
	const output: JSONContent[] = [];
	const stack: OpenBlock[] = [];
	let converted = false;

	const push = (node: JSONContent) => {
		const open = stack[stack.length - 1];
		if (!open) {
			output.push(node);
		} else if (open.elseContent === null) {
			open.ifContent.push(node);
		} else {
			open.elseContent.push(node);
		}
	};

	for (const child of children) {
		const tags = tagsOf(child);

		if (tags.length === 0) {
			push(child);
			continue;
		}
		if (tags.some((tag) => tag === null)) {
			return null;
		}

		const stripped = trimTrailingSpace(stripTags(child));
		const tagsOnly = holdsNothing(stripped);

		if (!tagsOnly) {
			const inlineConverted = convertInlineTags(child);
			if (inlineConverted) {
				push(inlineConverted);
				converted = true;
				continue;
			}
			if (tags.length !== 1 || tags[0]?.kind !== 'endif' || stack.length === 0) {
				return null;
			}
			push(stripped);
		}

		for (const tag of tags) {
			if (!tag) {
				return null;
			}
			if (tag.kind === 'if') {
				stack.push({ variable: tag.variable, ifContent: [], elseContent: null });
				continue;
			}
			const open = stack[stack.length - 1];
			if (!open) {
				return null;
			}
			if (tag.kind === 'else') {
				if (open.elseContent !== null) {
					return null;
				}
				open.elseContent = [];
				continue;
			}
			if (open.ifContent.length === 0) {
				return null;
			}
			stack.pop();
			converted = true;
			push(
				conditionalBlock({
					variable: open.variable,
					ifContent: open.ifContent,
					elseContent: open.elseContent ?? undefined,
				})
			);
		}
	}

	return stack.length === 0 && converted ? output : null;
};

const convertNode = (node: JSONContent): JSONContent => {
	if (!node.content || node.content.length === 0) {
		return node;
	}
	const content = node.content.map(convertNode);
	if (!BLOCK_CONTAINERS.has(node.type ?? '')) {
		return { ...node, content };
	}
	return { ...node, content: convertBlockList(content) ?? content };
};

export const documentHasLiquidTags = (document: JSONContent): boolean =>
	TAG.test(flatText(document));

export const importLiquidConditionals = (document: JSONContent): JSONContent =>
	documentHasLiquidTags(document) ? convertNode(document) : document;
